import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { MdFeedback } from "react-icons/md";
import { FiChevronDown, FiSettings, FiPrinter, FiSave } from "react-icons/fi";
import "./Paid.css";

const Paid = () => {
  const [showPayDropdown, setShowPayDropdown] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showCreateBillPopup, setShowCreateBillPopup] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [uploadedBills, setUploadedBills] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("All");
  const [billDateFilter, setBillDateFilter] = useState("All Dates");
  const [fromDate, setFromDate] = useState("-");
  const [toDate, setToDate] = useState("-");
  const [suppliers, setSuppliers] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [error, setError] = useState(null);

  const payDropdownRef = useRef(null);
  const addDropdownRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const uploadPopupRef = useRef(null);
  const filterPopupRef = useRef(null);
  const createPopupRef = useRef(null);

  // Memoize getSupplierName to prevent recreation
  const getSupplierName = useCallback((supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) return 'Unknown Supplier';
    return supplier.supplier_display_name || `${supplier.first_name || ''} ${supplier.last_name || ''}`.trim();
  }, [suppliers]);

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/api/suppliers/", {
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error(`Failed to fetch suppliers: ${res.status}`);
        const data = await res.json();
        setSuppliers(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching suppliers:", err);
        setError(`Failed to load suppliers: ${err.message}`);
      }
    };
    fetchSuppliers();
  }, []);

  // Fetch paid bills from backend
  useEffect(() => {
    const fetchPaidBills = async () => {
      try {
        const response = await fetch('https://backend.hira-tech.co.uk/api/bills/paid/', {
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`Failed to fetch paid bills: ${response.status}`);
        const data = await response.json();
        // Format API data to match frontend expectations
        const formattedBills = data.map(bill => {
          const billAmount = parseFloat(bill.amount || bill.total || 0).toFixed(2);
          return {
            id: bill.id,
            supplier: getSupplierName(bill.supplier),
            date: new Date(bill.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            duedate: new Date(bill.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            amount: `£${billAmount}`,
            status: bill.status,
            daysOverdue: bill.daysOverdue || "-",
            action: "Show Payments"
          };
        });
        setUploadedBills(formattedBills);
        setFilteredBills(formattedBills);
        setSelectedBills(new Array(formattedBills.length).fill(false));
        setError(null);
      } catch (error) {
        console.error("Error fetching paid bills:", error);
        setError(`Failed to load paid bills: ${error.message}`);
      }
    };
    fetchPaidBills();
  }, [getSupplierName]);

  const handleOutsideClick = (e) => {
    if (payDropdownRef.current && !payDropdownRef.current.contains(e.target)) {
      setShowPayDropdown(false);
    }
    if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
      setShowAddDropdown(false);
    }
    if (batchDropdownRef.current && !batchDropdownRef.current.contains(e.target) && !filterPopupRef.current?.contains(e.target)) {
      setShowBatchDropdown(false);
    }
    if (uploadPopupRef.current && !uploadPopupRef.current.contains(e.target)) {
      setShowUploadPopup(false);
    }
    if (createPopupRef.current && !createPopupRef.current.contains(e.target)) {
      setShowCreateBillPopup(false);
    }
    if (filterPopupRef.current && !filterPopupRef.current.contains(e.target)) {
      setShowFilterPopup(false);
    }
  };

  useEffect(() => {
    if (showPayDropdown || showAddDropdown || showBatchDropdown || showUploadPopup || showFilterPopup || showCreateBillPopup) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showPayDropdown, showAddDropdown, showBatchDropdown, showUploadPopup, showFilterPopup, showCreateBillPopup]);

  const handleUploadClick = (e) => {
    e.preventDefault();
    setShowUploadPopup(true);
  };

  const handleCreateClick = () => {
    setShowCreateBillPopup(true);
  };

  const handleShowPayments = (id) => {
    setSelectedBillId(id);
    setShowPaymentDetails(true);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelectedBills(new Array(filteredBills.length).fill(newSelectAll));
  };

  const handleSelectBill = (index) => {
    const newSelectedBills = [...selectedBills];
    newSelectedBills[index] = !newSelectedBills[index];
    setSelectedBills(newSelectedBills);
    setSelectAll(newSelectedBills.every((selected) => selected));
  };

  const handleSupplierChange = (e) => {
    setSupplierFilter(e.target.value);
  };

  const handleBillDateChange = (e) => {
    setBillDateFilter(e.target.value);
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFromDate(value);
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    setToDate(value);
  };

  const applyFilters = () => {
    let filtered = [...uploadedBills];

    if (supplierFilter !== "All") {
      filtered = filtered.filter((bill) => bill.supplier === supplierFilter);
    }

    const today = new Date();
    const getBillDate = (bill) => {
      const [day, month, year] = bill.date.split('/');
      return new Date(`${year}-${month}-${day}`);
    };

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstDayOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastQuarter = new Date(today.getFullYear(), Math.floor((today.getMonth() - 3) / 3) * 3, 1);
    const lastYear = new Date(today.getFullYear() - 1, 0, 1);

    switch (billDateFilter) {
      case "Since 365 Days ago":
        const oneYearAgo = new Date();
        oneYearAgo.setDate(today.getDate() - 365);
        filtered = filtered.filter((bill) => getBillDate(bill) >= oneYearAgo);
        break;
      case "This Month":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfMonth);
        break;
      case "This Quarter":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfQuarter);
        break;
      case "This Year":
        filtered = filtered.filter((bill) => getBillDate(bill) >= firstDayOfYear);
        break;
      case "Last Month":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return (
            billDate.getFullYear() === lastMonth.getFullYear() &&
            billDate.getMonth() === lastMonth.getMonth()
          );
        });
        break;
      case "Last Quarter":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return (
            billDate >= lastQuarter &&
            billDate < firstDayOfQuarter
          );
        });
        break;
      case "Last Year":
        filtered = filtered.filter((bill) => {
          const billDate = getBillDate(bill);
          return billDate.getFullYear() === lastYear.getFullYear();
        });
        break;
      case "Custom":
        if (fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          filtered = filtered.filter((bill) => {
            const billDate = getBillDate(bill);
            return billDate >= from && billDate <= to;
          });
        }
        break;
      default:
        // "All dates" or anything not handled just means no filtering
    }

    setFilteredBills(filtered);
    setSelectedBills(new Array(filtered.length).fill(false));
    setSelectAll(false);
    setShowBatchDropdown(false);
  };

  return (
    <div className="bills-container">
      {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      <h1>Expenses</h1>
      <nav className="nav-bar">
        <NavLink to="/admin/pk-expenses" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Expenses</NavLink>
        <NavLink to="/admin/bills" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Bills</NavLink>
        <NavLink to="/admin/pk-suppliers" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Suppliers</NavLink>
      </nav>

      <div className="bills-header-row">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <p className="bills-title">Bills</p>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <NavLink to="/admin/add-bill" className="header-btn"><MdFeedback /> Give Feedback</NavLink>
            <div className="dropdowns-btn" onClick={() => setShowPayDropdown(!showPayDropdown)}>
              Pay bills <FiChevronDown />
              {showPayDropdown && (
                <div ref={payDropdownRef} className="dropdown-meu" style={{ width: "170px" }}>
                  <button>Pay selected</button>
                </div>
              )}
            </div>
            <div className="dropdown-button" onClick={() => setShowAddDropdown(!showAddDropdown)}>
              Add bill <FiChevronDown />
              {showAddDropdown && (
                <div ref={addDropdownRef} className="dropdown-meu" style={{ width: "200px", height: "120px" }}>
                  <button onClick={handleUploadClick}>Upload from computer</button>
                  <button onClick={handleCreateClick} disabled>Create bill</button>
                  <button disabled>Create recurring bill</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bills-top-bar">
        <div className="bills-status-filters">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <NavLink to="/admin/bills" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>For review</NavLink>
              <NavLink to="/admin/unpaid" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>Unpaid</NavLink>
              <NavLink to="/admin/paid" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")} style={{ backgroundColor: "#d3d3d3" }}>Paid</NavLink>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div className="dropdowns-btn filters" onClick={() => setShowBatchDropdown(!showBatchDropdown)} ref={batchDropdownRef}>
                <span className="batch-actions">Filters</span> <FiChevronDown />
                {showBatchDropdown && (
                  <div ref={filterPopupRef} className="dropdown-menu" style={{ width: "400px", padding: "1rem" }} onClick={(e) => e.stopPropagation()}>
                    <div>
                      <label>Supplier</label>
                      <select value={supplierFilter} onChange={handleSupplierChange}>
                        <option value="All">All</option>
                        {suppliers.map((supplier) => (
                          <option key={supplier.id} value={getSupplierName(supplier.id)}>
                            {getSupplierName(supplier.id)}
                          </option>
                        ))}
                      </select>
                      <label>Bill date</label>
                      <div className="date-container">
                        <select value={billDateFilter} onChange={handleBillDateChange}>
                          <option value="All Dates">All Dates</option>
                          <option value="Custom">Custom</option>
                          <option value="Since 365 Days ago">Since 365 Days ago</option>
                          <option value="This Month">This Month</option>
                          <option value="This Quarter">This Quarter</option>
                          <option value="This Year">This Year</option>
                          <option value="Last Month">Last Month</option>
                          <option value="Last Quarter">Last Quarter</option>
                          <option value="Last Year">Last Year</option>
                        </select>
                      </div>
                      {billDateFilter === "Custom" && (
                        <>
                          <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{ marginRight: "0.5rem" }}
                          />
                          <span style={{ marginRight: "0.5rem" }}>to</span>
                          <input
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                          />
                        </>
                      )}
                      <div className="buttons" style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem", gap: "1rem" }}>
                        <button className="reset-btn" onClick={() => { setSupplierFilter("All"); setBillDateFilter("All Dates"); setFromDate(""); setToDate(""); setFilteredBills(uploadedBills); }}>Reset</button>
                        <button className="apply-btn" onClick={applyFilters}>Apply</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button className="action-filter" style={{ width: "200px", fontSize: "15px", borderRadius: "15px", backgroundColor: "#ccc", color: "black", height: "35px", marginTop: "15px" }}>
                Bill date: {billDateFilter === "Custom" ? "Custom" : billDateFilter}
              </button>
            </div>
          </div>
        </div>
        <div className="bills-actions">
          <div className="icon-actions">
            <button className="icon-action-btn" title="Settings"><FiSettings /></button>
            <button className="icon-action-btn" title="Print"><FiPrinter /></button>
            <button className="icon-action-btn" title="Save"><FiSave /></button>
          </div>
        </div>
      </div>

      <div className="billss-table">
        <div className="tables-row tables-header">
          <div className="checkbox-col"><input type="checkbox" checked={selectAll} onChange={handleSelectAll} /></div>
          <div className="supplier-col">SUPPLIER</div>
          <div className="bill-date-col">BILL DATE</div>
          <div className="due-date-col">DUE DATE</div>
          <div className="bill-amount-col">BILL AMOUNT</div>
          <div className="open-balance-col">OPEN BALANCE</div>
          <div className="status-col">STATUS</div>
          <div className="action-col">ACTION</div>
        </div>
        {filteredBills.length === 0 && (
          <div className="no-bills-message">
            <h3>No paid bills</h3>
          </div>
        )}
        {filteredBills.map((bill, index) => (
          <div key={bill.id} className="table-row">
            <div className="checkbox-col"><input type="checkbox" checked={selectedBills[index]} onChange={() => handleSelectBill(index)} /></div>
            <div className="supplier-col">{bill.supplier}</div>
            <div className="bill-date-col">{bill.date}</div>
            <div className="due-date-col">{bill.duedate}</div>
            <div className="bill-amount-col">{bill.amount}</div>
            <div className="open-balance-col">-</div>
            <div className="status-col">{bill.status}</div>
            <div className="action-col">
              <button className="actionbtn" onClick={() => handleShowPayments(bill.id)}>Show Payments</button>
            </div>
          </div>
        ))}
        {showPaymentDetails && selectedBillId && (
          <div className="payment-details">
            <p>Payment details for bill ID: {selectedBillId}</p>
            <button className="actionbtn" onClick={() => setShowPaymentDetails(false)}>Hide Details</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Paid;